import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import {
  OPEN_COOKIE_SETTINGS_EVENT,
  acceptAll,
  hasStoredConsent,
  rejectAll,
} from '../../utils/cookies/cookieConsent';
import {
  MOBILE_BOTTOM_NAV_HEIGHT_PX,
  MOBILE_BOTTOM_NAV_INSET_PX,
} from '../../styles/navigation/BottomNavigationStyles';
import CookieSettingsDialog from './CookieSettingsDialog';
import { FONT_SIZES } from '../../Theme';

/** Above mobile brand bar (10009) and bottom tab bar (9999). */
export const COOKIE_BANNER_Z_INDEX = 10020;
/** Settings dialog must stack above the consent banner. */
export const COOKIE_SETTINGS_Z_INDEX = COOKIE_BANNER_Z_INDEX + 10;

const pillSx = {
  borderRadius: 999,
  textTransform: 'none',
  fontWeight: 700,
  px: 2,
  py: 1,
  minHeight: 40,
  flex: 1,
  fontSize: FONT_SIZES.bodySm,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
};

function CookieConsentBanner() {
  const [bannerVisible, setBannerVisible] = useState(() => !hasStoredConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hasMobileBottomNav, setHasMobileBottomNav] = useState(() => (
    typeof document !== 'undefined'
    && document.body.classList.contains('has-mobile-bottom-nav')
  ));

  useEffect(() => {
    const openSettings = () => {
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    const syncBottomNav = () => {
      setHasMobileBottomNav(document.body.classList.contains('has-mobile-bottom-nav'));
    };
    syncBottomNav();
    const observer = new MutationObserver(syncBottomNav);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const dismissBanner = () => setBannerVisible(false);

  const handleAcceptAll = () => {
    acceptAll();
    dismissBanner();
  };

  const handleRejectAll = () => {
    rejectAll();
    dismissBanner();
  };

  const handleOpenSettings = () => setSettingsOpen(true);

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    if (hasStoredConsent()) {
      dismissBanner();
    }
  };

  const mobileBottomOffset = hasMobileBottomNav
    ? `calc(16px + ${MOBILE_BOTTOM_NAV_HEIGHT_PX + MOBILE_BOTTOM_NAV_INSET_PX}px + env(safe-area-inset-bottom, 0px))`
    : 16;

  return (
    <>
      {bannerVisible && !settingsOpen && (
        <Paper
          elevation={0}
          role="dialog"
          aria-label="Cookie consent"
          aria-describedby="cookie-consent-description"
          sx={(theme) => ({
            position: 'fixed',
            zIndex: COOKIE_BANNER_Z_INDEX,
            bgcolor: 'background.dark',
            color: 'common.white',
            boxSizing: 'border-box',
            left: { xs: 16, sm: 0 },
            right: { xs: 16, sm: 0 },
            bottom: { xs: mobileBottomOffset, sm: 0 },
            width: { xs: 'auto', sm: '100%' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 'none' },
            borderRadius: { xs: 3, sm: '16px 16px 0 0' },
            p: { xs: 2, sm: 2.5 },
            boxShadow: {
              xs: `0 12px 40px ${alpha(theme.palette.common.black, 0.28)}`,
              sm: `0 -4px 24px ${alpha(theme.palette.common.black, 0.18)}`,
            },
          })}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Typography
              id="cookie-consent-description"
              variant="body2"
              sx={{
                color: 'common.white',
                lineHeight: 1.45,
                fontSize: FONT_SIZES.bodySm,
                minWidth: 0,
                flex: 1,
                m: 0,
                textAlign: 'left',
              }}
            >
              Millet uses cookies &amp; similar technologies for our website, sign-in, security,
              payments you request, and optional features like maps. Manage your preferences
              {' '}
              <Box
                component="button"
                type="button"
                onClick={handleOpenSettings}
                sx={{
                  display: 'inline',
                  background: 'none',
                  border: 'none',
                  p: 0,
                  m: 0,
                  color: 'inherit',
                  font: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  verticalAlign: 'baseline',
                }}
              >
                here
              </Box>
              .
              {' '}
              See our
              {' '}
              <Link
                component={RouterLink}
                to="/cookie-notice"
                underline="always"
                sx={{ color: 'common.white' }}
              >
                Cookie Notice
              </Link>
              .
            </Typography>
            <IconButton
              aria-label="Reject all cookies"
              onClick={handleRejectAll}
              size="small"
              sx={(theme) => ({
                flexShrink: 0,
                width: 32,
                height: 32,
                mt: -0.5,
                mr: -0.5,
                color: alpha(theme.palette.common.white, 0.72),
                '&:hover': {
                  color: 'common.white',
                  bgcolor: alpha(theme.palette.common.white, 0.08),
                },
              })}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: '100%',
              mt: 1.5,
            }}
          >
            <Button
              variant="contained"
              disableElevation
              onClick={handleOpenSettings}
              sx={(theme) => ({
                ...pillSx,
                bgcolor: alpha(theme.palette.common.white, 0.1),
                color: 'common.white',
                border: `1px solid ${alpha(theme.palette.common.white, 0.28)}`,
                '&:hover': {
                  ...pillSx['&:hover'],
                  bgcolor: alpha(theme.palette.common.white, 0.16),
                  borderColor: alpha(theme.palette.common.white, 0.4),
                },
              })}
            >
              Manage
            </Button>
            <Button
              variant="contained"
              disableElevation
              onClick={handleAcceptAll}
              sx={{
                ...pillSx,
                bgcolor: 'accent.main',
                color: 'common.white',
                '&:hover': {
                  ...pillSx['&:hover'],
                  bgcolor: 'accent.main',
                  color: 'common.white',
                  filter: 'brightness(0.96)',
                },
              }}
            >
              Ok
            </Button>
          </Stack>
        </Paper>
      )}

      <CookieSettingsDialog
        open={settingsOpen}
        onClose={handleSettingsClose}
        zIndex={COOKIE_SETTINGS_Z_INDEX}
      />
    </>
  );
}

export default CookieConsentBanner;
